/**
 * Licences, tools and languages of a mission matched against their
 * referential by name key. Both lists were typed by hand, so the same item can
 * carry an edge space or another case: the referential holds " Chaussure S2",
 * the mission "Chaussure S2". An exact match left the box unchecked on entering
 * edit mode, and the next save dropped the item from the mission.
 */

/** Same item for the mission and its referential: edge spaces and case aside. */
export const requirementKey = (value) =>
  typeof value === 'string' ? value.trim().toLocaleLowerCase('fr') : '';

const keyedValues = (values) => {
  const byKey = new Map();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const key = requirementKey(value);
    if (key && !byKey.has(key)) byKey.set(key, value);
  });
  return byKey;
};

/**
 * The checkbox options of the edit form: one per referential name, checked
 * when the mission holds it. A checked option takes the spelling stored on the
 * mission, so saving it again sends the value unchanged. A mission value the
 * referential does not list stays checked, at the end, as stored.
 */
export const checkMissionRequirements = (referentialNames, missionValues) => {
  const missionByKey = keyedValues(missionValues);
  const listedKeys = new Set();
  const options = [];

  (Array.isArray(referentialNames) ? referentialNames : []).forEach((name) => {
    const key = requirementKey(name);
    if (!key || listedKeys.has(key)) return;
    listedKeys.add(key);
    options.push(
      missionByKey.has(key)
        ? { name: missionByKey.get(key), checked: true }
        : { name, checked: false },
    );
  });

  missionByKey.forEach((value, key) => {
    if (!listedKeys.has(key)) options.push({ name: value, checked: true });
  });

  return options;
};

/** Whether two lists hold the same items, whatever their order, spaces or case. */
export const haveSameRequirements = (left, right) => {
  const leftKeys = keyedValues(left);
  const rightKeys = keyedValues(right);
  if (leftKeys.size !== rightKeys.size) return false;
  return [...leftKeys.keys()].every((key) => rightKeys.has(key));
};

/**
 * The qualification options with the mission's own one checked, as new
 * objects. « Annuler » only leaves the edit mode while the options are built
 * once per mission: without this, the next edit showed the qualification of
 * the cancelled one, although the form still held the mission's.
 */
export const checkMissionQualification = (qualifications, missionTitle) =>
  (qualifications || []).map((qualification) => ({
    ...qualification,
    checked: qualification.name === missionTitle,
  }));
